import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useCallback } from 'react';

import { useFeature } from '~/modules/root/hook/use-feature';

export interface BookMeetingGateIntent {
    /** Professional the meeting is with, and who receives the request. */
    professionalUrlSegment: string;
    /** Page the request is about: a project, a profile, a product. */
    siteTreeId: number;
    /** Named in the enquiry body, e.g. a project title. */
    itemTitle?: string;
    /**
     * Bookable Client Director sessions, when the professional has a connected
     * calendar. Empty means the working-week request flow instead.
     */
    sessionIds?: number[];
    /**
     * Team member to open on, for a CTA that names a person (the Our Team
     * cards). Deliberately the TeamMember row id, not the member account id:
     * the row id is already on the page that renders the tile, whereas the
     * account id is not, and there is no reason for a profile page to carry
     * account identifiers for its staff. The gate maps one to the other from
     * the availability it loads anyway.
     */
    preferredTeamMemberId?: number;
    /**
     * That person's name, as the page already shows it.
     *
     * Sent with the id so the pane can name them on the first frame. The id
     * alone means the pane has to wait for the roster to say who it belongs to,
     * and until then it can only fall back to the practice: the reader clicked
     * a face and watched the panel say "Meet with <the company>" before
     * correcting itself.
     */
    preferredTeamMemberName?: string;
    /** Face or logo for the modal's left pane, so it matches the CTA. */
    paneImage?: string;
    /**
     * The page knows the practice shows more than one person, so the loading
     * state can reserve the picker rather than let it drop in.
     *
     * Only set where the count is already on the page. Guessing it is worse
     * than leaving it out: most professionals have no public team member at
     * all, and a reserved field that then collapses moves the calendar 59px.
     */
    expectsTeamPicker?: boolean;
    /**
     * The practice, as the page already knows it, so the left pane can name and
     * badge them on the first frame instead of holding the ArchiPro brand pane
     * up for the length of the availability fetch.
     *
     * Only the practice, never the person: whether a given face is bookable is
     * decided by the payload being fetched, so naming one here would risk
     * showing a name that the answer then contradicts. The person's name lands
     * when it is known to be true. See BC-5174.
     */
    professionalName?: string;
    companyLogo?: string | null;
    logoBackgroundColor?: string | null;
}

/**
 * What the reader was trying to book before the gate stopped them, persisted so
 * it survives a social round-trip's full page reload. Session-scoped: an
 * abandoned intent must not follow them into a later visit and open a booking
 * they have forgotten asking for.
 */
export const BOOK_MEETING_GATE_INTENT_KEY = 'book-meeting-gate-intent';
export const BOOK_MEETING_GATE_OPEN_KEY = 'book-meeting-gate-open';

export const bookMeetingGateIntentAtom =
    atomWithStorage<BookMeetingGateIntent | null>(
        BOOK_MEETING_GATE_INTENT_KEY,
        null,
        createJSONStorage<BookMeetingGateIntent | null>(() => sessionStorage),
    );

/**
 * Whether the gate is showing, deliberately in memory rather than in storage.
 *
 * A persisted flag reopened the modal on the next page the reader visited, with
 * the professional they had been looking at before — so a booking for one
 * practice appeared over another's profile. The intent is still persisted, so a
 * social round-trip can pick the errand back up; only the "currently on screen"
 * part is per-page.
 */
export const bookMeetingGateOpenAtom = atom(false);

/** Read-only access for surfaces that only need to know a booking is pending. */
export const useBookMeetingGateIntent = () =>
    useAtomValue(bookMeetingGateIntentAtom);

/**
 * Opening the gate, for CTAs that render in bulk (a Book a meeting button on
 * every Our Team card) and must not re-render when the gate's state changes.
 */
export const useOpenBookMeetingGate = () => {
    const setIntent = useSetAtom(bookMeetingGateIntentAtom);
    const setOpen = useSetAtom(bookMeetingGateOpenAtom);

    return useCallback(
        (intent: BookMeetingGateIntent) => {
            setIntent(intent);
            setOpen(true);
        },
        [setIntent, setOpen],
    );
};

/** Full access, for the gate host itself. */
export const useBookMeetingGate = () => {
    const [intent, setIntent] = useAtom(bookMeetingGateIntentAtom);
    const [isOpen, setOpen] = useAtom(bookMeetingGateOpenAtom);

    const open = useCallback(
        (next: BookMeetingGateIntent) => {
            setIntent(next);
            setOpen(true);
        },
        [setIntent, setOpen],
    );

    const close = useCallback(() => {
        setOpen(false);
        setIntent(null);
    }, [setOpen, setIntent]);

    return { intent, isOpen, open, close };
};

/**
 * Whether the booking gate can actually open.
 *
 * Two flags have to agree. `book_meeting_open_access` is the feature itself,
 * on its own rollout (training and marketing adoption pace it, not the
 * revamp); `revamp_2026` carries the auth step the gate finishes on, and
 * {@link BookMeetingGateModal} is mounted behind it so a stale sessionStorage
 * intent cannot open a gate on a build meant to have none. A CTA gated on the
 * first alone would set the intent and mount nothing, so every entry point
 * reads this rather than the feature flag on its own.
 */
export const useBookMeetingGateEnabled = () => {
    // Both read unconditionally: `&&` between two hook calls short-circuits the
    // second, which breaks the rules of hooks.
    const featureEnabled = useFeature('book_meeting_open_access');
    const authGateEnabled = useFeature('revamp_2026');

    return featureEnabled && authGateEnabled;
};
